import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Bell, ChevronDown, CircleHelp, Menu, Plus } from "lucide-react";
import ProfileModal from "./ProfileModal";
import HelpModal from "./HelpModal";
import NotificationModal from "./NotificationModal";
import { useAccess } from "../../access/AccessContext";

const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/providers": "Hospitals",
  "/patients": "Patients",
  "/patners": "Partners",
  "/iam/users": "Admin users",
  "/iam/roles": "Roles & permissions",
  "/iam/provider-users": "Hospital admins",
  "/trust": "Trust",
  "/support": "Support",
  "/general": "General settings",
  "/notification": "Notifications",
};

const titleFor = (pathname) =>
  PAGE_TITLES[pathname] ||
  (pathname.startsWith("/subscriptions/") && "Subscription details") ||
  pathname
    .split("/")
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" / ") ||
  "Dashboard";

const Topbar = ({ onMenuClick }) => {
  const [openPanel, setOpenPanel] = useState(null);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const panelRef = useRef(null);
  const { showForm } = useSelector((state) => state.providerReducer);
  const { profile } = useSelector((state) => state.userReducer);
  const { access, can } = useAccess();

  const toggle = (panel) => setOpenPanel((current) => (current === panel ? null : panel));

  const handleClickOutside = useCallback((event) => {
    if (panelRef.current && !panelRef.current.contains(event.target)) setOpenPanel(null);
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const openProviderForm = () => {
    if (showForm && searchParams.get("id")) {
      dispatch({ type: "MAKE_TENANT_NULL" });
      navigate("/providers");
    }
    dispatch({ type: "Change_Show_Form_State", payload: !showForm });
  };

  // Hide record ids (e.g. /subscriptions/<tenantId>) from the breadcrumb.
  const crumbs = pathname.split("/").filter((c) => c && !/^[a-f0-9]{24}$/i.test(c));
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ");
  const initials = `${profile?.firstName?.[0] || ""}${profile?.lastName?.[0] || ""}`.toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <button onClick={onMenuClick} className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 lg:hidden" aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold text-slate-900">{titleFor(pathname)}</h1>
          <p className="hidden truncate text-xs text-slate-500 sm:block">
            Central admin
            {crumbs.map((c) => (
              <span key={c}>
                <span className="mx-1.5 text-slate-300">/</span>
                <span className="capitalize">{c === "iam" ? "IAM" : c === "providers" ? "Hospitals" : c.replace(/-/g, " ")}</span>
              </span>
            ))}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2" ref={panelRef}>
        {pathname.startsWith("/providers") && !showForm && can("providers", "create") && (
          <button
            onClick={openProviderForm}
            className="mr-2 inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600"
          >
            <Plus className="h-4 w-4" /> Onboard hospital
          </button>
        )}
        <div className="relative">
          <button onClick={() => toggle("help")} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700" aria-label="Help">
            <CircleHelp className="h-5 w-5" />
          </button>
          {openPanel === "help" && <HelpModal />}
        </div>
        <div className="relative">
          <button onClick={() => toggle("notifications")} className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700" aria-label="Notifications">
            <Bell className="h-5 w-5" />
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" />
          </button>
          {openPanel === "notifications" && <NotificationModal />}
        </div>
        <div className="relative ml-1">
          <button
            onClick={() => toggle("profile")}
            className="flex items-center gap-2.5 rounded-lg py-1 pl-1 pr-2 hover:bg-slate-100"
          >
            {profile?.imageUrl ? (
              <img src={profile.imageUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                {initials || "A"}
              </span>
            )}
            <span className="hidden text-left leading-tight md:block">
              <span className="block text-sm font-medium text-slate-900">{fullName || "Admin"}</span>
              <span className="block text-xs text-slate-500">{access?.roleName || "…"}</span>
            </span>
            <ChevronDown className="hidden h-4 w-4 text-slate-400 md:block" />
          </button>
          {openPanel === "profile" && <ProfileModal />}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
